import { compareNullableDate, compareText, matchesQuery, selectedAllows } from './directory-filter';

export const RELEASE_STATUSES = ['draft', 'open', 'approved', 'frozen', 'released'] as const;
export type ReleaseStatus = (typeof RELEASE_STATUSES)[number];

export const RELEASE_STATUS_LABELS: Record<ReleaseStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  approved: 'Approved',
  frozen: 'Frozen',
  released: 'Released',
};

export const RELEASE_SORT_OPTIONS = [
  { value: 'target_date', label: 'Target date' },
  { value: '-created_at', label: 'Newest' },
  { value: 'created_at', label: 'Oldest' },
  { value: 'name', label: 'Name A-Z' },
  { value: '-name', label: 'Name Z-A' },
  { value: 'status', label: 'Status' },
] as const;

export const RELEASE_DEFAULT_ORDER = 'target_date';

export type ReleaseSlim = {
  id: string;
  name: string;
  tag: string | null;
  status: ReleaseStatus;
  branch_name: string | null;
  target_date: string | null;
  item_count: number;
  version: number;
  created_at: string;
  updated_at: string;
};

export type ReleaseItem = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  project_identifier: string;
  project_name: string;
  state_id: string;
  state_name: string;
  state_group: string;
  type: string;
  priority: string;
  due_date: string | null;
};

export const CADENCE_KINDS = [
  'risk',
  'test_plan',
  'engineering_readiness',
  'qa_readiness',
  'operational_readiness',
  'deployment_readiness',
  'sign_off',
  'rollout_plan',
  'rollback',
  'post_release_verification',
] as const;
export type CadenceKind = (typeof CADENCE_KINDS)[number];

export const CADENCE_LABELS: Record<CadenceKind, string> = {
  risk: 'Risk',
  test_plan: 'Test plan',
  engineering_readiness: 'Engineering readiness',
  qa_readiness: 'QA readiness',
  operational_readiness: 'Operational readiness',
  deployment_readiness: 'Deployment readiness',
  sign_off: 'Sign-off',
  rollout_plan: 'Rollout plan',
  rollback: 'Rollback',
  post_release_verification: 'Post-release verification',
};

export const RISK_VERDICTS = ['low', 'medium', 'high'] as const;
export const SIGN_OFF_VERDICTS = ['go', 'go_with_conditions', 'no_go'] as const;

export type CadenceItem = {
  kind: CadenceKind;
  status: 'todo' | 'done';
  verdict: string | null;
  note: string | null;
  owner_id: string | null;
  wiki_page_id: string | null;
  wiki_path: string;
  version: number;
};

export type ReleaseFull = ReleaseSlim & {
  wiki_slug: string;
  hidden_item_count: number;
  items: ReleaseItem[];
  cadence: CadenceItem[];
};

export function isCadenceKind(value: string): value is CadenceKind {
  return (CADENCE_KINDS as readonly string[]).includes(value);
}

export function cadenceLabel(kind: string): string {
  return isCadenceKind(kind) ? CADENCE_LABELS[kind] : kind;
}

export function signOffVerdictLabel(verdict: string): string {
  if (verdict === 'go') return 'Go';
  if (verdict === 'go_with_conditions') return 'Go with conditions';
  if (verdict === 'no_go') return 'No-go';
  return verdict;
}

const NEXT_RELEASE_STATUSES: Record<ReleaseStatus, readonly ReleaseStatus[]> = {
  draft: ['draft', 'open', 'approved', 'released'],
  open: ['open', 'draft', 'approved', 'released'],
  approved: ['approved', 'open', 'released'],
  frozen: ['frozen', 'open', 'approved', 'released'],
  released: ['released'],
};

export function isReleaseStatus(value: string): value is ReleaseStatus {
  return (RELEASE_STATUSES as readonly string[]).includes(value);
}

export function nextReleaseStatuses(status: ReleaseStatus): readonly ReleaseStatus[] {
  return NEXT_RELEASE_STATUSES[status];
}

export function releaseStatusNeedsBranch(status: ReleaseStatus): boolean {
  return status !== 'draft';
}

export function isReleaseLocked(status: ReleaseStatus | string | undefined): boolean {
  return status === 'approved' || status === 'frozen' || status === 'released';
}

export function releaseStatusLabel(status: string): string {
  return isReleaseStatus(status) ? RELEASE_STATUS_LABELS[status] : status;
}

export function filterReleases(
  items: ReleaseSlim[],
  opts: { query: string | null; statuses: string[]; orderBy: string | null },
): ReleaseSlim[] {
  const filtered = items.filter((item) => {
    if (!selectedAllows(opts.statuses, item.status)) return false;
    return matchesQuery(opts.query, [item.name, item.tag, item.branch_name, item.status]);
  });
  const orderBy = opts.orderBy ?? RELEASE_DEFAULT_ORDER;
  return filtered.slice().sort((left, right) => {
    if (orderBy === 'name') return compareText(left.name, right.name);
    if (orderBy === '-name') return compareText(right.name, left.name);
    if (orderBy === 'created_at') return left.created_at.localeCompare(right.created_at);
    if (orderBy === '-created_at') return right.created_at.localeCompare(left.created_at);
    if (orderBy === 'status') {
      const byStatus = RELEASE_STATUSES.indexOf(left.status) - RELEASE_STATUSES.indexOf(right.status);
      return byStatus !== 0 ? byStatus : compareText(left.name, right.name);
    }
    const byDate = compareNullableDate(left.target_date, right.target_date);
    return byDate !== 0 ? byDate : compareText(left.name, right.name);
  });
}
